/**
 * El cupón de bienvenida tal como viaja del servidor a las pantallas de
 * confirmación (kiosko y online). Módulo sin I/O: lo importan componentes de
 * cliente, así que no puede traer Supabase.
 */
export type WelcomeCoupon = {
  code: string
  pct: number
  endsAt: string
  /** Entrada a "Mis descuentos" en la cuenta Redy (el kiosko lo pinta como QR). */
  discountsUrl?: string | null
}

/**
 * Lee `welcomeCoupon` de un cuerpo de respuesta. Cualquier forma inesperada es
 * "no hay cupón": mostrarle al cliente un código a medias es peor que no
 * mostrar nada.
 */
export function parseWelcomeCoupon(body: unknown): WelcomeCoupon | null {
  const c = (body as { welcomeCoupon?: unknown } | null)?.welcomeCoupon as Record<string, unknown> | null | undefined
  if (!c || typeof c !== "object") return null
  if (typeof c.code !== "string" || !/^[A-Z0-9]{4,20}$/.test(c.code)) return null
  if (typeof c.pct !== "number" || typeof c.endsAt !== "string") return null
  if (Number.isNaN(new Date(c.endsAt).getTime())) return null
  // Solo http(s): el kiosko lo convierte en QR y un `javascript:` o un esquema
  // raro no tiene por qué llegar al teléfono del cliente.
  const discountsUrl = typeof c.discountsUrl === "string" && /^https?:\/\//.test(c.discountsUrl) ? c.discountsUrl : null
  return { code: c.code, pct: c.pct, endsAt: c.endsAt, discountsUrl }
}

/**
 * Cupón de bienvenida de una orden "paga en caja": existe pero está INACTIVO
 * hasta que la orden se pague, así que viaja sin código.
 */
export type WelcomePending = { pct: number; discountsUrl: string | null }

export function parseWelcomePending(body: unknown): WelcomePending | null {
  const c = (body as { welcomePending?: unknown } | null)?.welcomePending as Record<string, unknown> | null | undefined
  if (!c || typeof c !== "object" || typeof c.pct !== "number") return null
  const discountsUrl = typeof c.discountsUrl === "string" && /^https?:\/\//.test(c.discountsUrl) ? c.discountsUrl : null
  return { pct: c.pct, discountsUrl }
}

/** Último día válido para mostrar: `ends_at` es EXCLUSIVO. */
export function welcomeCouponLastDay(endsAt: string, locale: string): string {
  return new Date(new Date(endsAt).getTime() - 1).toLocaleDateString(locale, { day: "numeric", month: "short" })
}
